import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { Globe, ChevronDown, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import logo from "./assets/naim-logo.png";
import { auth, __FIREBASE_CONFIG_OK__, getOrCreateRecaptcha, signInWithPhone, disposeRecaptcha } from "./lib/firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, sendEmailVerification } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getUserIpAddress } from "./utils/ipAddress";
import { SUPPORTED_LANGUAGES, changeLanguage, getCurrentLanguage } from "./i18n";

// Helper function to sanitize Firebase error messages
const sanitizeErrorMessage = (error, t) => {
  const message = error?.message || "";

  // Check for quota exceeded errors
  if (message.includes("quota-exceeded") || message.includes("auth/quota-exceeded")) {
    return t('auth.limitExceeded');
  }

  // Hide detailed Firebase error messages
  if (message.startsWith("Firebase: Error")) {
    // Extract just the error code if available
    const codeMatch = message.match(/auth\/([^)]+)/);
    if (codeMatch) {
      const errorCode = codeMatch[1];
      // Map common error codes to user-friendly messages
      const errorMap = {
        "invalid-phone-number": t('auth.invalidPhoneNumber'),
        "invalid-verification-code": t('auth.invalidVerificationCode'),
        "code-expired": t('auth.codeExpired'),
        "too-many-requests": t('auth.authErrorTooManyRequests'),
        "network-request-failed": t('auth.authErrorNetworkRequestFailed'),
      };
      return errorMap[errorCode] || t('auth.anErrorOccurred');
    }
    return t('auth.anErrorOccurred');
  }

  return message || t('auth.anErrorOccurred');
};

export default function SignIn() {
  const [mode, setMode] = useState("email"); // "email" | "phone"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [phone, setPhone] = useState(""); // E.164 without "+" from PhoneInput
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);

  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [showReset, setShowReset] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(getCurrentLanguage());
  const { t: translate } = useTranslation();

  // Clean up reCAPTCHA widget if user navigates away
  useEffect(() => {
    return () => {
      disposeRecaptcha().catch(() => { });
    };
  }, []);

  // ------- Email/password auth -------
  const handleEmailAuth = async (signup = false) => {
    setError("");
    setStatus("");
    setLoading(true);
    try {
      if (signup) {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        try {
          await sendEmailVerification(cred.user);
          // Don't show status message - the VerifyEmail page will show after redirect
        } catch (emailErr) {
          console.error("Failed to send verification email:", emailErr);
        }
        // User is automatically signed in by Firebase, so they'll be redirected to VerifyEmail page by ProtectedLayout
        return;
      } else {
        await signInWithEmailAndPassword(auth, email.trim(), password);
      }
    } catch (err) {
      setError(sanitizeErrorMessage(err, translate) || translate('auth.authErrorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    setError("");
    setStatus("");
    if (!resetEmail.trim()) {
      setError(translate('auth.enterEmailToReset'));
      return;
    }
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail.trim());
      setStatus(translate('auth.passwordResetEmailSent'));
    } catch (err) {
      setError(sanitizeErrorMessage(err, translate) || translate('auth.failedToSendResetEmail'));
    } finally {
      setLoading(false);
    }
  };

  // ------- Phone auth (with shared invisible reCAPTCHA) -------
  const sendOtp = async () => {
    if (!__FIREBASE_CONFIG_OK__) {
      return alert("Firebase is not configured. Add REACT_APP_* keys to .env.local and restart.");
    }
    setError("");
    setStatus("");
    setLoading(true);
    try {
      // PhoneInput returns digits only; prepend '+' to make E.164
      const e164 = phone.startsWith("+") ? phone : `+${phone}`;

      // Get user's IP address
      const ipAddress = await getUserIpAddress();

      // Check SMS rate limit BEFORE sending
      const functions = getFunctions();
      const checkSmsRateLimit = httpsCallable(functions, "checkSmsRateLimit");

      // console.log("Checking SMS rate limit...", { phone: e164, ip: ipAddress });
      const rateLimitResult = await checkSmsRateLimit({
        phoneNumber: e164,
        ipAddress
      });

      if (!rateLimitResult.data.allowed) {
        const resetDate = new Date(rateLimitResult.data.resetTime);
        throw new Error(
          `Too many SMS attempts. Please try again after ${resetDate.toLocaleString()}.`
        );
      }

      // console.log("Rate limit check passed:", rateLimitResult.data);
      setStatus(`${rateLimitResult.data.attemptsRemaining} ${translate('auth.smsAttemptsRemaining')}`);

      // Reuse/create a single invisible widget. The container is rendered below.
      const verifier = getOrCreateRecaptcha("recaptcha-container", "invisible");
      const confirmation = await signInWithPhone(e164, verifier);
      // Stash it globally just like before (so verifyOtp stays simple)
      window.confirmationResult = confirmation;
      setIsOtpSent(true);
      setStatus(translate('auth.otpSentSuccess'));
    } catch (err) {
      console.error("Failed to send OTP:", err);
      setError(sanitizeErrorMessage(err, translate) || translate('auth.failedToSendSms'));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setError("");
    setLoading(true);
    try {
      if (!window.confirmationResult) throw new Error(translate('auth.noOtpSession'));
      await window.confirmationResult.confirm(otp);
    } catch (err) {
      setError(sanitizeErrorMessage(err, translate) || translate('auth.invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 justify-center items-center relative">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          className="flex items-center gap-2 bg-white px-3 py-2 rounded-full border border-gray-200 shadow-sm hover:shadow-md transition"
        >
          <Globe size={16} className="text-green-600" />
          <span className="text-green-600 font-medium text-sm">
            {SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguage)?.nativeName?.slice(0, 8) || 'EN'}
          </span>
          <ChevronDown size={14} className="text-green-600" />
        </button>
        {showLanguageDropdown && (
          <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 z-50 max-h-80 overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  setSelectedLanguage(lang.code);
                  changeLanguage(lang.code);
                  setShowLanguageDropdown(false);
                }}
                className={`w-full px-4 py-2 text-left flex justify-between items-center hover:bg-green-50 transition ${lang.code === selectedLanguage ? 'bg-green-50 text-green-600' : 'text-gray-700'
                  }`}
              >
                <span>{lang.nativeName}</span>
                {lang.code === selectedLanguage && <Check size={16} className="text-green-600" />}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md border border-gray-100">
        <div className="flex flex-col items-center mb-6">
          <img src={logo} alt="Naim Web" className="h-14 mb-3" />
          <h1 className="text-2xl font-semibold text-naimGray">{translate('auth.signInToNaim')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {translate('auth.accessCatalog')}
          </p>
        </div>

        <div className="flex justify-center mb-6 space-x-4">
          <button
            onClick={() => setMode("email")}
            className={`px-4 py-2 rounded-lg font-medium transition ${mode === "email" ? "bg-naimGreen text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            {translate('auth.email')}
          </button>
          <button
            onClick={() => setMode("phone")}
            className={`px-4 py-2 rounded-lg font-medium transition ${mode === "phone" ? "bg-naimGreen text-white shadow-md" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
          >
            {translate('auth.phone')}
          </button>
        </div>

        {mode === "email" ? (
          <div className="space-y-4">
            <input
              type="email"
              placeholder={translate('auth.email')}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-naimGreen outline-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEmailAuth(false)}
              autoComplete="email"
            />
            <input
              type="password"
              placeholder={translate('auth.password')}
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-naimGreen outline-none"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleEmailAuth(false)}
              autoComplete="current-password"
            />
            <div className="flex justify-between">
              <button
                onClick={() => handleEmailAuth(false)}
                disabled={loading}
                className="bg-naimGreen text-white px-4 py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-60"
              >
                {loading ? translate('auth.signingIn') : translate('auth.signIn')}
              </button>
              <button
                onClick={() => handleEmailAuth(true)}
                disabled={loading}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition disabled:opacity-60"
              >
                {loading ? translate('auth.creating') : translate('auth.signUp')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {!isOtpSent ? (
              <>
                <PhoneInput
                  country={"se"}
                  value={phone}
                  onChange={(v) => setPhone(v)}
                  enableSearch
                  inputClass="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-naimGreen outline-none"
                  disableDropdown={false}
                />
                <button
                  onClick={sendOtp}
                  disabled={loading || !phone}
                  className="w-full bg-naimGreen text-white py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-60"
                >
                  {loading ? translate('auth.sending') : translate('auth.sendCode')}
                </button>
              </>
            ) : (
              <>
                <input
                  type="text"
                  placeholder={translate('auth.enterSmsCode')}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-naimGreen outline-none"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !loading && otp && verifyOtp()}
                  inputMode="numeric"
                />
                <button
                  onClick={verifyOtp}
                  disabled={loading || !otp}
                  className="w-full bg-naimGreen text-white py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-60"
                >
                  {loading ? translate('auth.verifying') : translate('auth.confirmCode')}
                </button>
              </>
            )}
            {/* Recaptcha container (invisible). Kept in DOM for reuse across flows. */}
            <div id="recaptcha-container"></div>
          </div>
        )}

        {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
        {status && <p className="text-green-600 text-sm mt-2">{status}</p>}

        <div className="mt-6">
          <button
            onClick={() => setShowReset(!showReset)}
            className="text-sm text-green-600 hover:text-green-700 transition"
          >
            {showReset ? translate('auth.hidePasswordReset') : translate('auth.forgotPassword')}
          </button>
          {showReset && (
            <div className="mt-3 space-y-2">
              <input
                type="email"
                placeholder={translate('auth.emailForReset')}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-naimGreen outline-none"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !loading && resetEmail.trim() && handleReset()}
                autoComplete="email"
              />
              <button
                onClick={handleReset}
                disabled={loading}
                className="w-full bg-gray-100 text-gray-800 py-2 rounded-lg hover:bg-gray-200 transition disabled:opacity-60"
              >
                {loading ? translate('auth.sending') : translate('auth.sendResetLink')}
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-gray-400 text-xs mt-8">
          © {new Date().getFullYear()} Naim Web. {translate('auth.allRightsReserved')}
        </p>
      </div>
    </div>
  );
}
